<template>
  <box
    class="config-card"
    :title="t('Configuração de Antecipação')"
    icon="pi pi-cog"
  >
    <!-- Loading state -->
    <div v-if="isLoading" class="loading-state">
      <spinner style="width: 30px; height: 30px" />
    </div>

    <!-- Error state -->
    <div v-else-if="loadError" class="error-state">
      <i class="pi pi-exclamation-triangle"></i>
      <span>{{ loadError }}</span>
      <Button
        data-testid="anticipation-config-retry-button"
        @click="loadConfig"
        severity="secondary"
        size="small"
        :label="t('Tentar novamente')"
        icon="pi pi-refresh"
        text
      />
    </div>

    <!-- Config form -->
    <div v-else-if="config" class="config-content">
      <!-- Provider info -->
      <div v-if="config.provider_config" class="provider-info">
        <div class="info-item">
          <span class="info-label">{{ t('Antecipacao automatica de cartao') }}:</span>
          <Tag
            :value="config.provider_config.creditCardAutomaticAnticipation ? t('Ativada') : t('Desativada')"
            :severity="config.provider_config.creditCardAutomaticAnticipation ? 'success' : 'secondary'"
          />
        </div>
      </div>

      <Divider />

      <!-- Local config form -->
      <div class="config-form">
        <div class="field">
          <div class="field-row">
            <label for="enabled">{{ t('Antecipacao habilitada') }}</label>
            <toggle
              data-testid="anticipation-config-enabled-toggle"
              v-model="localConfig.enabled"
              elemId="enabled"
              :disabled="isSaving"
            />
          </div>
          <small class="field-help">{{ t('Habilita ou desabilita antecipacoes para esta subconta') }}</small>
        </div>

        <div class="field">
          <div class="field-row">
            <label for="autoCreditCard">{{ t('Antecipacao automatica de cartao de credito') }}</label>
            <toggle
              data-testid="anticipation-config-auto-credit-card-toggle"
              v-model="localConfig.auto_credit_card"
              elemId="autoCreditCard"
              :disabled="isSaving"
            />
          </div>
          <small class="field-help">{{ t('Antecipa automaticamente cobranças de cartão de crédito') }}</small>
        </div>

        <div class="field">
          <label for="minAmount">{{ t('Valor minimo para antecipacao') }}</label>
          <input-currency
            data-testid="anticipation-config-min-amount-input"
            elem-id="anticipation_min_amount"
            v-model="localConfig.min_amount_display"
            :disabled="isSaving"
          />
          <small class="field-help">{{ t('Valor minimo em reais para solicitar antecipacao') }}</small>
        </div>
      </div>

      <!-- Save button -->
      <div class="actions">
        <Button
          data-testid="anticipation-config-save-button"
          @click="saveConfig"
          :loading="isSaving"
          :disabled="isSaving || !hasChanges"
          severity="primary"
          :label="t('Salvar Configuração')"
          icon="pi pi-save"
        />
      </div>

      <!-- Last update info -->
      <div v-if="config.local_config?.updated_at" class="last-update">
        <small>{{ t('Ultima atualizacao') }}: {{ formatDateTime(config.local_config.updated_at) }}</small>
      </div>
    </div>

    <!-- No config state -->
    <div v-else class="no-config">
      <i class="pi pi-info-circle"></i>
      <span>{{ t('Nenhuma configuracao disponivel') }}</span>
    </div>
  </box>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { eventBus } from '../../eventBus';
import { log } from '../../utils/DebugLogger';
import { useAnticipations } from '../../composables/useAnticipations';

type Id = number | string;

interface LocalConfig {
  enabled: boolean;
  auto_credit_card: boolean;
  min_amount?: number;
  updated_at?: string;
}

interface ProviderConfig {
  creditCardAutomaticAnticipation?: boolean;
  [key: string]: unknown;
}

interface AnticipationConfig {
  local_config?: LocalConfig;
  provider_config?: ProviderConfig;
  [key: string]: unknown;
}

interface EditableConfig {
  enabled: boolean;
  auto_credit_card: boolean;
  min_amount_display: number;
}

const props = withDefaults(
  defineProps<{
    subaccountId?: Id | null;
    autoLoad?: boolean;
  }>(),
  {
    subaccountId: null,
    autoLoad: true,
  }
);

const emit = defineEmits<{
  (e: 'config-updated', response: unknown): void;
  (e: 'config-loaded', response: AnticipationConfig): void;
  (e: 'error', error: unknown): void;
}>();

const { t } = useI18n();
const { getConfig, updateConfig } = useAnticipations();

// State
const isLoading = ref<boolean>(false);
const isSaving = ref<boolean>(false);
const loadError = ref<string | null>(null);
const config = ref<AnticipationConfig | null>(null);
const originalConfig = ref<EditableConfig | null>(null);

// Local editable config
const localConfig = ref<EditableConfig>({
  enabled: false,
  auto_credit_card: false,
  min_amount_display: 0 // In reais for display
});

// Computed: Check if config has changes
const hasChanges = computed<boolean>(() => {
  if (!originalConfig.value) return false;

  const orig = originalConfig.value;
  return (
    localConfig.value.enabled !== orig.enabled ||
    localConfig.value.auto_credit_card !== orig.auto_credit_card ||
    localConfig.value.min_amount_display !== orig.min_amount_display
  );
});

// Watch for subaccount changes
watch(() => props.subaccountId, (newId) => {
  if (newId && props.autoLoad) {
    loadConfig();
  }
}, { immediate: false });

// Methods
const loadConfig = async (): Promise<void> => {
  if (!props.subaccountId) {
    log('AnticipationConfigCard', 'loadConfig', 'No subaccount ID');
    return;
  }

  log('AnticipationConfigCard', 'loadConfig', 'Loading config', props.subaccountId);
  isLoading.value = true;
  loadError.value = null;

  try {
    const response = await getConfig(Number(props.subaccountId)) as unknown as AnticipationConfig;
    log('AnticipationConfigCard', 'loadConfig', 'Response', response);

    config.value = response;

    // Parse local config
    const local = response.local_config || ({} as LocalConfig);
    localConfig.value = {
      enabled: local.enabled || false,
      auto_credit_card: local.auto_credit_card || false,
      min_amount_display: (local.min_amount || 0) / 100 // Convert cents to reais
    };

    // Store original for comparison
    originalConfig.value = { ...localConfig.value };

    emit('config-loaded', response);
  } catch (error) {
    log('AnticipationConfigCard', 'loadConfig', 'Error', error);
    const message = error instanceof Error ? error.message : String(error);
    loadError.value = message || t('Erro ao carregar configuracao');
    emit('error', error);
  } finally {
    isLoading.value = false;
  }
};

const saveConfig = async (): Promise<void> => {
  if (!props.subaccountId) return;

  log('AnticipationConfigCard', 'saveConfig', 'Saving config', localConfig.value);
  isSaving.value = true;

  try {
    // Convert display amount back to cents
    const configData = {
      enabled: localConfig.value.enabled,
      auto_credit_card: localConfig.value.auto_credit_card,
      min_amount: Math.round(localConfig.value.min_amount_display * 100) // Convert reais to cents
    };

    const response = await updateConfig(Number(props.subaccountId), configData);
    log('AnticipationConfigCard', 'saveConfig', 'Response', response);

    // Update original config to match saved values
    originalConfig.value = { ...localConfig.value };

    eventBus.emit('globalMessage.success', t('Configuração salva com sucesso'));
    emit('config-updated', response);

    // Reload to get fresh data
    await loadConfig();
  } catch (error) {
    log('AnticipationConfigCard', 'saveConfig', 'Error', error);
    const message = error instanceof Error ? error.message : String(error);
    eventBus.emit('globalMessage.error', message || t('Erro ao salvar configuracao'));
    emit('error', error);
  } finally {
    isSaving.value = false;
  }
};

const formatDateTime = (dateStr?: string | null): string => {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  return date.toLocaleString('pt-BR');
};

// Lifecycle
onMounted(() => {
  log('AnticipationConfigCard', 'onMounted', 'Component mounted', {
    subaccountId: props.subaccountId,
    autoLoad: props.autoLoad
  });

  if (props.subaccountId && props.autoLoad) {
    loadConfig();
  }
});

// Expose methods
defineExpose({
  loadConfig,
  saveConfig,
  refresh: loadConfig
});
</script>

<style scoped>
.config-card {
  height: 100%;
}

.card-title {
  display: flex;
  align-items: center;
  font-size: 1rem;
  font-weight: 600;
}

.loading-state,
.error-state,
.no-config {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  padding: 2rem;
  text-align: center;
  color: var(--text-color-secondary);
}

.error-state i,
.no-config i {
  font-size: 2rem;
  color: var(--red-500);
}

.no-config i {
  color: var(--text-color-secondary);
}

.config-content {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.provider-info {
  background: var(--surface-ground);
  padding: 0.75rem;
  border-radius: 6px;
}

.info-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.info-label {
  font-size: 0.875rem;
  color: var(--text-color-secondary);
}

.config-form {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}

.field {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.field-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.field-row label {
  font-weight: 500;
  color: var(--text-color);
}

.field-help {
  color: var(--text-color-secondary);
  font-size: 0.75rem;
}

.actions {
  display: flex;
  justify-content: flex-end;
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid var(--surface-border);
}

.last-update {
  text-align: right;
  color: var(--text-color-secondary);
  margin-top: 0.5rem;
}
</style>
